import {Article} from '../article';
import {EnumCorridorColor} from '../../../enum/enum-corridor-color';
import {PricingItem} from '../pricing-item';
import {DeviceConsumablePricingItem} from '../device-consumable-pricing-item';
import {generateUniqueId} from '../../../../utils/math-utils';
import {EnumPricingItemType} from '../pricing-item-type';
import {PackPricingItem} from '../pack-pricing-item';
import {ConsumablePricingItem} from '../consumable-pricing-item';
import {DevicePricingItem} from '../device-pricing-item';
import {BillingLine} from '../../../billing-line/billing-line.model';
import {DeviceConsumableWithSuppPricingItem} from '../device-consumable-with-supp-pricing-item';
import {ProductPricingV2} from '../../../productv2/productpricingv2.model';
import {ProductPricing, InitialArticleInfo} from 'quote-domain-core';
import {ProductV2} from '../../../productv2/productv2.model';
import {AtomicProductV2} from '../../../productv2/atomic-productv2.model';
import {KitProductV2} from '../../../productv2/kit-productv2.model';
import {PackProductV2} from '../../../productv2/pack-productv2.model';
import {PriceList} from '../../../productv2/pricelist.model';
import {EnumArticleType} from '../../../enum/enum-article-type';

export class PricingItemMapper {


    constructor(private readonly mapDeviceConsumableToDeviceConsumableWithSupp: boolean) {
    }

    mapProductToPricingItemWithCustomerContract(product: ProductV2, customerContract?: BillingLine, idx?: string): PricingItem {
        let pricingItem = this.mapProductToPricingItem(product, idx);
        if (pricingItem instanceof ConsumablePricingItem) {
            return pricingItem.copyWith({
                consumable: this.updateArticleWithCustomerContract(pricingItem.consumable, customerContract),
                consoType: customerContract.consoType,
                consumableExtraPrice: String(customerContract?.unitPriceExtra || '0.00'),
            });
        } else if (pricingItem instanceof DevicePricingItem) {
            return pricingItem.copyWith({
                device: this.updateArticleWithCustomerContract(pricingItem.device, customerContract)
            });
        }
        return pricingItem;
    }


    private updateArticleWithCustomerContract(article: Article, customerContract: BillingLine) {
        return article.copyWith({
            label: customerContract?.articleName,
            appliedPrice: String(customerContract?.unitPrice || '0.00'),
            isRenewal: true,
            oldAppliedPrice: String(customerContract?.unitPrice || '0.00')
        });
    }


    mapProductToPricingItem(product: ProductV2, idx?: string): PricingItem {

        if (product instanceof AtomicProductV2) {
            const generatedId = generateUniqueId();
            switch (product.type) {
                case EnumArticleType.DEVICE:
                case EnumArticleType.TEXTILE:
                    return new DevicePricingItem().copyWith({
                        id: generateUniqueId(),
                        matchingId: generatedId,
                        family: product.family,
                        isValid: false,
                        device: this.mapProductToArticle(product, product.priceList, product.productDetails),
                        pricingItemType: EnumPricingItemType.DEVICE,
                        isCmc: product.isCmc,
                        displayOrder: idx
                    });

                case EnumArticleType.CONSUMABLE:
                    return new ConsumablePricingItem().copyWith({
                        id: generateUniqueId(),
                        matchingId: generatedId,
                        family: product.family,
                        isValid: false,
                        consumable: this.mapProductToArticle(product, product.priceList, product.productDetails),
                        pricingItemType: EnumPricingItemType.CONSUMABLE,
                        isCmc: product.isCmc,
                        displayOrder: idx
                    });

                default:
                    return null;
            }
        } else if (product instanceof KitProductV2) {
            const generatedId = generateUniqueId();

            let pricingItemChild = {
                id: generatedId,
                matchingId: generatedId,
                family: product.device.family,
                isValid: false,
                device: this.mapProductToArticle(product.device, product.device.priceList, product.device.productDetails),
                consumable: this.mapProductToArticle(product.consumable, product.consumable.priceList, product.consumable.productDetails),
                specificities: this.mapSpecificities(product),
                franchise: product.allowance,
                pricingItemType: this.mapDeviceConsumableToDeviceConsumableWithSupp ? EnumPricingItemType.DEVICE_CONSUMABLE_WITH_SUPP : EnumPricingItemType.DEVICE_CONSUMABLE,
                isCmc: product.isCmc,
                additionalConsumablePrice: '',
                displayOrder: idx
            };

            if (this.mapDeviceConsumableToDeviceConsumableWithSupp) {
                return new DeviceConsumableWithSuppPricingItem().copyWith(pricingItemChild);
            } else {
                return new DeviceConsumablePricingItem().copyWith(pricingItemChild);
            }

        } else if (product instanceof PackProductV2) {

            const generatedId = generateUniqueId();
            switch (product.type) {
                case EnumArticleType.DEVICE:
                    let productDetails = {
                        ...product.consumable.productDetails,
                        pack: {code: product.consumable.code, label: product.consumable.label}
                    };
                    return new PackPricingItem().copyWith({
                        id: generateUniqueId(),
                        family: product.consumable.family,
                        isValid: false,
                        device: this.mapProductToArticle(product.device, product.priceList, productDetails),
                        pricingItemType: EnumPricingItemType.PACK,
                        isCmc: product.isCmc,
                        displayOrder: idx
                    });
                case 'CONSUMABLE':
                    return new ConsumablePricingItem().copyWith({
                        id: generateUniqueId(),
                        matchingId: generatedId,
                        family: product.family,
                        isValid: false,
                        consumable: this.mapProductToArticle(product.consumable, product.priceList, product.productDetails),
                        pricingItemType: EnumPricingItemType.CONSUMABLE,
                        isCmc: product.isCmc,
                        displayOrder: idx
                    });

            }

        }

        return null;

    }

    mapSpecificities(product: KitProductV2): Record<string, any> {
        return {
            'ratioW': product.ratioh4s,
            'ratioM': product.ratiof4s,
            'ratioM/W': product.ratiohf4s,
            'franchise': product.allowance
        };
    }


    private mapProductToArticle(product: AtomicProductV2, priceList: PriceList, productDetails: any): Article {

        return new Article().copyWith({
            label: product.label,
            codeElis: product.code,
            articleDetails: productDetails ?? product.productDetails,
            productPricings: this.mapProductPricing(priceList.productPricings),
            initialArticleInfo: this.mapInitialArticleInfo(priceList.productPricings),
            appliedPrice: '0.00',
            appliedPriceColor: EnumCorridorColor.GRAY,
            status: (product instanceof AtomicProductV2) ? product.status : null,
            groupingCode: priceList.groupingCode,
            isCmcWithStandardProductPricing: product['isCmcWithStandardProductPricing'],
            pcc: product.pcc,
            pccType: product.pccType,
            ppe: product.ppe,
            replacementPrice: product.replacementPrice,
            grossMarginRate: product.grossMarginRate,
            washLifetime: product.washLifetime,
            washRate: product.washRate,
            catalogPrice: priceList.productPricings[0].p90
        });
    }


    mapProductPricing(productPricingsV2: ProductPricingV2[]): ProductPricing[] {
        let productPricings: ProductPricing[] = [];

        productPricingsV2?.forEach((productPricing: ProductPricingV2) => {
            Object.setPrototypeOf(productPricing, ProductPricingV2.prototype);
            const mappedPricing = Object.assign(new ProductPricing(), {
                id: productPricing.id,
                p10: productPricing.p10,
                p10p50: productPricing.p10p50,
                p50: productPricing.p50,
                p50p90: productPricing.p50p90,
                p90: productPricing.p90,
                r1090: '0',
                r3090: '0',
                r5090: '0',
                r7090: '0',
                type: null,
                productPricingDetails: productPricing.productPricingDetails
            });
            productPricings.push(mappedPricing);

        });

        return productPricings;
    }

    mapInitialArticleInfo(productPricingsV2: ProductPricingV2[]): InitialArticleInfo {
        const productPricing = productPricingsV2?.[0];
        if (!productPricing) {
            return undefined;
        }
        Object.setPrototypeOf(productPricing, ProductPricingV2.prototype);
        return Object.assign(new InitialArticleInfo(), {
            frequencies: productPricing.frequencies,
            invoicingModes: productPricing.invoicingModes,
            numberOfChanges: productPricing.numberOfChanges,
            contractDuration: productPricing.contractLength,
            quantityMin: productPricing.minQuantity,
            quantityMax: productPricing.maxQuantity
        });
    }

}
